import type { LensFilterValues } from '~/types/leLens';

import { addDays, lastCompleteDay, monday } from '../ProductPerformance/model';

export const LOYALTY_SOCIETE_PAGE_ID = 'loyalty-societe';

export type LoyaltyView =
  | 'overview'
  | 'members'
  | 'tier-migration'
  | 'points-earn-burn'
  | 'points-purchased'
  | 'partner-connection';
export const LOYALTY_VIEWS: ReadonlyArray<{ value: LoyaltyView; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'members', label: 'Members' },
  { value: 'tier-migration', label: 'Tier Migration' },
  { value: 'points-earn-burn', label: 'Points Earn&Burn' },
  { value: 'points-purchased', label: 'Points Purchased' },
  { value: 'partner-connection', label: 'Partner Connection' },
];

/** The workbook's filters, declared in svc-reporting's native-specs/loyalty-societe.json and keyed by filter id. */
export const FILTER_DEFINITIONS = [
  {
    key: 'loyalty_region',
    label: 'Loyalty region',
    views: ['overview', 'members', 'tier-migration', 'points-earn-burn', 'points-purchased'],
  },
  { key: 'luxplus_member', label: 'LuxPlus member', views: ['overview', 'members'] },
  { key: 'tier_name', label: 'Tier', views: ['overview', 'members', 'points-earn-burn'] },
  { key: 'category', label: 'Category', views: ['points-earn-burn'] },
  { key: 'partner', label: 'Partner', views: ['partner-connection'] },
  { key: 'currency_code', label: 'Currency', views: ['points-purchased'] },
  { key: 'redeemed_after_purchase', label: 'Redeemed points after purchase', views: ['points-purchased'] },
] as const;
type FilterKey = (typeof FILTER_DEFINITIONS)[number]['key'];

export type Granularity = 'day' | 'week' | 'month';
export const GRANULARITIES: ReadonlyArray<{ value: Granularity; label: string }> = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

export type LoyaltyFilters = {
  view: LoyaltyView;
  from: string;
  to: string;
  granularity: Granularity;
  selections: Record<FilterKey, Array<string>>;
};

// The workbook's custom date range defaults to the 30 days ending on the latest loaded day.
const DEFAULT_WINDOW_DAYS = 30;
// Société points are worth half a cent each in the workbook's $ conversions.
export const POINT_VALUE_AUD = 0.005;

export function defaultFilters(view: LoyaltyView = 'overview', now = new Date()): LoyaltyFilters {
  const to = lastCompleteDay(now);
  return {
    view,
    from: addDays(to, 1 - DEFAULT_WINDOW_DAYS),
    to,
    granularity: 'day',
    selections: Object.fromEntries(FILTER_DEFINITIONS.map(({ key }) => [key, [] as Array<string>])) as Record<
      FilterKey,
      Array<string>
    >,
  };
}

const isDay = (value: string | null): value is string =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && new Date(`${value}T00:00:00Z`).toISOString().startsWith(value);

export function filtersError(filters: LoyaltyFilters, now = new Date()): string | null {
  if (!isDay(filters.from) || !isDay(filters.to)) {
    return 'Choose valid dates in YYYY-MM-DD format.';
  }
  if (filters.from > filters.to) {
    return 'The start date must be on or before the end date.';
  }
  if (filters.to > lastCompleteDay(now)) {
    return `Loyalty data is complete to ${lastCompleteDay(now)}. Choose an earlier end date.`;
  }
  return null;
}

export function parseFilters(params: URLSearchParams, now = new Date()): LoyaltyFilters {
  const requested = params.get('tab');
  const view = LOYALTY_VIEWS.some((item) => item.value === requested) ? (requested as LoyaltyView) : 'overview';
  const filters = defaultFilters(view, now);
  if (isDay(params.get('from'))) {
    filters.from = params.get('from');
  }
  if (isDay(params.get('to'))) {
    filters.to = params.get('to');
  }
  const granularity = params.get('granularity');
  if (GRANULARITIES.some((item) => item.value === granularity)) {
    filters.granularity = granularity as Granularity;
  }
  for (const { key } of FILTER_DEFINITIONS) {
    if (!params.has(key)) {
      continue;
    }
    try {
      const value: unknown = JSON.parse(params.get(key));
      if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
        filters.selections[key] = value;
      }
    } catch {
      // A malformed selection keeps the default.
    }
  }
  return filters;
}

export function writeFilters(filters: LoyaltyFilters, current: URLSearchParams, now = new Date()): URLSearchParams {
  const params = new URLSearchParams(current);
  const defaults = defaultFilters(filters.view, now);
  params.set('tab', filters.view);
  for (const key of ['from', 'to', 'granularity'] as const) {
    if (filters[key] === defaults[key]) {
      params.delete(key);
    } else {
      params.set(key, filters[key]);
    }
  }
  for (const { key } of FILTER_DEFINITIONS) {
    const values = [...new Set(filters.selections[key])].sort();
    if (values.length) {
      params.set(key, JSON.stringify(values));
    } else {
      params.delete(key);
    }
  }
  return params;
}

/** An empty selection means every value. */
export function lensFilters(filters: LoyaltyFilters): LensFilterValues {
  const values: LensFilterValues = { period: { window: { from: filters.from, to: filters.to }, comparison: null } };
  for (const { key } of FILTER_DEFINITIONS) {
    if (filters.selections[key].length) {
      values[key] = filters.selections[key];
    }
  }
  return values;
}

const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(typeof value === 'object' && value && 'value' in value ? value.value : value);
  return Number.isFinite(parsed) ? parsed : null;
};
const text = (value: unknown): string =>
  value === null || value === undefined
    ? 'Unknown'
    : String(typeof value === 'object' && 'value' in value ? value.value : value);

export function firstRow(rows: ReadonlyArray<Record<string, unknown>>, keys: ReadonlyArray<string>) {
  return Object.fromEntries(keys.map((key) => [key, num(rows[0]?.[key])])) as Record<string, number | null>;
}

export type Grouped = { label: string; values: Record<string, number | null> };

export function groupRows(
  rows: ReadonlyArray<Record<string, unknown>>,
  x: string,
  measures: ReadonlyArray<string>,
): Array<Grouped> {
  const groups = new Map<string, Record<string, number | null>>();
  for (const row of rows) {
    const label = text(row[x]);
    const current = groups.get(label) ?? Object.fromEntries(measures.map((key) => [key, null]));
    for (const key of measures) {
      const value = num(row[key]);
      if (value !== null) {
        current[key] = (current[key] ?? 0) + value;
      }
    }
    groups.set(label, current);
  }
  return [...groups].map(([label, values]) => ({ label, values }));
}

export function bucketDay(day: string, granularity: Granularity): string {
  if (granularity === 'week') {
    return monday(day);
  }
  if (granularity === 'month') {
    return `${day.slice(0, 7)}-01`;
  }
  return day;
}

export type SeriesPoint = { date: string; series: string; value: number };

/** Only additive measures can be rolled up this way. */
export function bucketPoints(
  rows: ReadonlyArray<Record<string, unknown>>,
  dateKey: string,
  measure: string,
  granularity: Granularity,
  seriesKey?: string,
): Array<SeriesPoint> {
  const totals = new Map<string, SeriesPoint>();
  for (const row of rows) {
    const day = text(row[dateKey]).slice(0, 10);
    const value = num(row[measure]);
    if (value === null || !isDay(day)) {
      continue;
    }
    const date = bucketDay(day, granularity);
    const series = seriesKey ? text(row[seriesKey]) : measure;
    const id = `${date}|${series}`;
    const point = totals.get(id) ?? { date, series, value: 0 };
    point.value += value;
    totals.set(id, point);
  }
  return [...totals.values()].sort((a, b) => a.date.localeCompare(b.date) || a.series.localeCompare(b.series));
}

export const ratio = (of: number | null, per: number | null): number | null =>
  of === null || per === null || per === 0 ? null : of / per;

const TIER_ORDER = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Infinite'];
export const byTier = (a: Grouped, b: Grouped) => {
  const rank = (label: string) => (TIER_ORDER.includes(label) ? TIER_ORDER.indexOf(label) : TIER_ORDER.length);
  return rank(a.label) - rank(b.label) || a.label.localeCompare(b.label);
};

const BAND_START = (label: string) => {
  if (label === 'Top tier') {
    return Number.POSITIVE_INFINITY;
  }
  const match = /^(\d[\d,]*)/.exec(label);
  return match ? Number(match[1].replace(/,/g, '')) : Number.POSITIVE_INFINITY;
};
/** Bands sort by their numeric start; 'Top tier' (no next tier) goes last. */
export const byBand = (a: string, b: string) => BAND_START(a) - BAND_START(b) || a.localeCompare(b);

export function pivotByTier(rows: ReadonlyArray<Record<string, unknown>>, band: string, measure: string) {
  const bands = [...new Set(rows.map((row) => text(row[band])))].sort(byBand);
  const tiers = [...new Set(rows.map((row) => text(row.tier_name)))].sort((a, b) =>
    byTier({ label: a, values: {} }, { label: b, values: {} }),
  );
  const values = new Map<string, number>();
  for (const row of rows) {
    const value = num(row[measure]);
    if (value !== null) {
      const id = `${text(row[band])}|${text(row.tier_name)}`;
      values.set(id, (values.get(id) ?? 0) + value);
    }
  }
  return { bands, tiers, value: (bandLabel: string, tier: string) => values.get(`${bandLabel}|${tier}`) ?? 0 };
}

export type Migration = {
  month: string;
  dataType: 'Actual' | 'Forecast';
  kind: 'Count' | 'New' | 'Up' | 'Down';
  tier: string;
  members: number;
  net: number;
};

export function readMigrations(rows: ReadonlyArray<Record<string, unknown>>): Array<Migration> {
  return rows.flatMap((row) => {
    const match = /^(Actual|Forecast) · (Count|New|Up|Down) - (.+)$/.exec(text(row.migration));
    if (!match) {
      return [];
    }
    return [
      {
        month: text(row.month),
        dataType: match[1] as Migration['dataType'],
        kind: match[2] as Migration['kind'],
        tier: match[3],
        members: num(row.members) ?? 0,
        net: num(row.net_migration_value) ?? 0,
      },
    ];
  });
}

/** First day of the Sydney month `offset` months from the one holding `now`. */
export function monthStart(now: Date, offset = 0): string {
  const [year, month] = new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' })
    .format(now)
    .split('-')
    .map(Number);
  const date = new Date(Date.UTC(year, month - 1 + offset, 1));
  return date.toISOString().slice(0, 10);
}

export const sumMigrations = (
  migrations: ReadonlyArray<Migration>,
  keep: (migration: Migration) => boolean,
  measure: 'members' | 'net' = 'members',
) => migrations.filter(keep).reduce((total, migration) => total + migration[measure], 0);

export function migrationBreakdown(migrations: ReadonlyArray<Migration>, dataType: Migration['dataType']) {
  const kept = migrations.filter((row) => row.dataType === dataType && row.kind !== 'Count');
  const months = [...new Set(kept.map((row) => row.month))].sort();
  const order = ['New', 'Up', 'Down'];
  const labels = [...new Set(kept.map((row) => `${row.kind} - ${row.tier}`))].sort(
    (a, b) => order.indexOf(a.split(' - ')[0]) - order.indexOf(b.split(' - ')[0]) || a.localeCompare(b),
  );
  const rows: Array<Grouped> = labels.map((label) => ({
    label,
    values: Object.fromEntries(
      months.map((month) => [
        month,
        kept
          .filter((row) => row.month === month && `${row.kind} - ${row.tier}` === label)
          .reduce((total, row) => total + row.members, 0),
      ]),
    ),
  }));
  return { months, rows };
}
